import { useEffect } from 'react';
import { ACTION_TYPES, usePublicAccountSearchDispatch, usePublicAccountSearchState } from './public-account-search-context';
import PublicAccountAppService from '../../../../application/platform/apm/public-account/public-account-app-service';
import { IPublicAccountListResponse } from '../../../../model/apm/public-account/response/public-account-list-response';
import { IPublicAccountAddEditRequest } from '../../../../model/apm/public-account/request/public-account-add-edit-request';
import { IPublicAccountPagedRequest } from '../../../../model/apm/public-account/request/public-account-paged-request';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { parseNumberInput } from '../../../../utils/format';
import { useActionRunner } from '../../../../hooks/use-action-runner';
import { useSearchActions } from '../../../../hooks/use-search-actions';
import { useDocumentAttachments } from '../../components/use-document-attachments';
import { attachmentsRemovalNotice } from '../../components/document-utils';
import { parseIdFilter } from '../../../../utils/filter-options';

export const usePublicAccountSearchController = () => {
    const dispatch = usePublicAccountSearchDispatch();
    const state = usePublicAccountSearchState();
    const { isSaving, runSaving, runWithLoading, run, showSuccess, confirmDelete } = useActionRunner();
    const { updateParameters, triggerSearch, ...searchHandlers } = useSearchActions<IPublicAccountPagedRequest>(
        dispatch, ACTION_TYPES, { term: undefined, year: undefined, categoryId: undefined, subcategoryId: undefined },
    );
    const attachments = useDocumentAttachments({
        ownerId: state.drawer.publicAccount?.id,
        files: state.drawer.files,
        setFiles: (files: IPortalDocumentResponse[]) => dispatch({ type: ACTION_TYPES.setDrawerFiles, payload: files }),
        upload: PublicAccountAppService.uploadFile,
        remove: PublicAccountAppService.removeFile,
        onChange: triggerSearch,
    });

    useEffect(() => {
        run('Não foi possível carregar as categorias e os tipos de documento', async () => {
            const [categories, documentTypes] = await Promise.all([PublicAccountAppService.listCategories(), PublicAccountAppService.listDocumentTypes()]);
            dispatch({ type: ACTION_TYPES.setLookups, payload: { categories, documentTypes } });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-term') updateParameters({ term: value || undefined });
        if (name === 'input-year') updateParameters({ year: parseNumberInput(value) });
        // Trocar a categoria invalida a subcategoria escolhida
        if (name === 'select-category') updateParameters({ categoryId: parseIdFilter(value), subcategoryId: undefined });
        if (name === 'select-subcategory') updateParameters({ subcategoryId: parseIdFilter(value) });
    }

    // A listagem não traz os anexos, então a edição busca a publicação completa
    function openDrawer(row?: IPublicAccountListResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        runWithLoading('Não foi possível carregar a publicação', async () => {
            dispatch({ type: ACTION_TYPES.openDrawer, payload: await PublicAccountAppService.getById(row.id) });
        });
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    function handleSave(request: IPublicAccountAddEditRequest) {
        const editing = state.drawer.publicAccount;
        return runSaving('Não foi possível salvar a publicação', async () => {
            if (editing) {
                await PublicAccountAppService.edit(editing.id, request);
                showSuccess('Publicação atualizada com sucesso!');
                closeDrawer();
            } else {
                // Mantém o drawer aberto, agora em edição, para anexar os documentos
                dispatch({ type: ACTION_TYPES.openDrawer, payload: await PublicAccountAppService.add(request) });
                showSuccess('Publicação cadastrada! Agora anexe os documentos.');
            }
            triggerSearch();
        });
    }

    function handleDelete(row: IPublicAccountListResponse) {
        confirmDelete(`Tem certeza que deseja excluir a publicação "${row.description}"?${attachmentsRemovalNotice(row.fileCount)}`, () => run('Não foi possível excluir a publicação', async () => {
            await PublicAccountAppService.remove(row.id);
            showSuccess('Publicação excluída com sucesso!');
            triggerSearch();
        }));
    }

    return {
        isSaving,
        handleOnChange, ...searchHandlers,
        openDrawer, closeDrawer, handleSave, handleDelete, ...attachments,
    };
};
